import { useCallback, useState } from 'react';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import uniqBy from 'lodash/uniqBy';

import { useCurrentUser } from '../../../../../auth/hooks/useAuth';
import { useModal } from '../../../../../helpers/modals/hooks/useModal';
import { useToastNotification } from '../../../../../common/hooks/useToastNotification';

import { IconsEnum } from '../../../../../assets/icons/types';
import {
  ClassName,
  GADataType,
  I18nText,
  IsDisabled,
  IsLoading
} from '../../../../../types';
import {
  ArchivedFile,
  DownloadUrlsModalButtonItemDownloadUrlsProps,
  DownloadUrlsModalButtonResultDownloadUrlsProps,
  DownloadUrlsModalButtonStringDownloadUrlsProps,
  ItemWithDownloadUrls,
  ItemWithDownloadUrlsWithStringsArray,
  ItemWithResultDownloadUrls
} from './DownloadUrlsModalButton.types';

import { ArchivedFilesList } from './components/ArchivedFilesList';

import { AwsException } from '../../../../../api/common/utils/exceptions/AwsException';
import { downloadFile } from '../../../../../utils/downloadFile';
import { getFileNameFromURL } from '../../../../../utils/getFileNameFromURL';
import { Loading } from '../../../../../helpers/Loading';
import { parseAwsResponseError } from '../../../../../utils/parseAwsResponseError';
import { PureTooltipIconButtonHelper } from '../../../../../helpers/buttons/PureTooltipIconButtonHelper';
import { SimpleModal } from '../../../../../helpers/modals/SimpleModal';
import { TooltipPlacement } from '../../../../../helpers/tooltips/tooltipsConstants';
import { Translate } from '../../../../../helpers/Translate';

import { stringsKeys, words } from '../../../../../locales/keys';

import { AwsStorageClasses, CommonPermissions } from '../../../commonConstants';
import { FileAttachmentsPermissions } from '../../../../fileAttachments/fileAttachmentsConstants';

const DOWNLOAD_STARTED_TOAST_ID = 'download-strat-toast-id';

function isStringArray(array: unknown): array is string[] {
  return (
    Array.isArray(array) && array?.every((item) => typeof item === 'string')
  );
}

type DownloadUrlsModalButtonProps<Scope extends string> = {
  className?: ClassName;
  disabled?: IsDisabled;
  i18nText?: I18nText;
  i18nTextClassName?: ClassName;
  icon?: IconsEnum;
  iconClassName?: ClassName;
  isLoading?: IsLoading;
  scope: Scope;
  togglePreventModalClose?: () => void;
  tooltipI18nText?: I18nText;
  tooltipPlacement?: TooltipPlacement;
  tooltipSingleton?: boolean;
} & (
  | DownloadUrlsModalButtonStringDownloadUrlsProps<
      ItemWithDownloadUrlsWithStringsArray,
      Scope
    >
  | DownloadUrlsModalButtonItemDownloadUrlsProps<ItemWithDownloadUrls, Scope>
  | DownloadUrlsModalButtonResultDownloadUrlsProps<
      ItemWithResultDownloadUrls,
      Scope
    >
);

function DownloadUrlsModalButton<Scope extends string>({
  className,
  dataGa,
  disabled,
  downloadUrlsScope,
  i18nText,
  i18nTextClassName,
  icon,
  iconClassName,
  isLoading,
  itemClassName,
  refetchItemDownloadUrls,
  scope,
  togglePreventModalClose,
  tooltipI18nText,
  tooltipPlacement,
  tooltipSingleton
}: DownloadUrlsModalButtonProps<Scope> & GADataType) {
  const currentUser = useCurrentUser();

  const [archivedFiles, setArchivedFiles] = useState<ArchivedFile[]>([]);

  const { isOpen, showModal, hideModal } = useModal({
    onClose: () => setArchivedFiles([])
  });

  const handleClearArchivedFiles = () => setArchivedFiles([]);

  const handleHideModal = () => {
    handleClearArchivedFiles();
    togglePreventModalClose?.();
    hideModal();
  };

  const { showToastNotification, removeToastNotification } =
    useToastNotification({ autoDismiss: false });

  const withBlob = currentUser.hasPermissions(
    CommonPermissions.READ_DOWNLOADS_WITH_FETCH
  );

  const handleClick = useCallback<() => Promise<void>>(async () => {
    const archivedFilesArray: ArchivedFile[] = [];

    const refetchItemResponse = await refetchItemDownloadUrls();

    const itemId = refetchItemResponse?.data?.[scope]?.id;

    const itemUuid = refetchItemResponse?.data?.[scope]?.uuid;

    const itemOriginalFilename =
      refetchItemResponse?.data?.[scope]?.originalFilename;

    const downloadUrls =
      refetchItemResponse?.data?.[scope]?.[downloadUrlsScope];

    if (isEmpty(downloadUrls)) {
      return;
    }

    showToastNotification(
      <>
        <Translate id={words.downloading} />
        &nbsp;
        <Loading size="2xs" />
        <br />
        <Translate id={words.dontCloseOrRefresh} />
      </>,
      'success',
      DOWNLOAD_STARTED_TOAST_ID
    );

    togglePreventModalClose?.();

    const downloadItems = isStringArray(downloadUrls)
      ? map(downloadUrls, (downloadUrl) => ({
          id: itemId,
          uuid: itemUuid,
          className: itemClassName,
          url: downloadUrl,
          name: itemOriginalFilename
        }))
      : downloadUrls;

    for (const { id, uuid, url, className, name } of downloadItems) {
      await downloadFile({
        downloadFile: {
          name: name || getFileNameFromURL(url),
          file: url
        },
        parseResponseError: parseAwsResponseError,
        onError: (error) => {
          if (
            error instanceof AwsException &&
            (error as AwsException).awsStorageClass ===
              AwsStorageClasses.DEEP_ARCHIVE
          ) {
            archivedFilesArray.push({
              id,
              uuid,
              url,
              className,
              name
            });
          }
        },
        withBlob
      });

      await new Promise((resolve) => setTimeout(resolve, 500));
    }

    removeToastNotification(DOWNLOAD_STARTED_TOAST_ID);

    setArchivedFiles(uniqBy(archivedFilesArray, 'uuid'));

    if (
      !isEmpty(archivedFilesArray) &&
      currentUser.hasPermissions(
        FileAttachmentsPermissions.READ_RESTORE_S3_OBJECTS_MODAL
      )
    ) {
      showModal();
    } else {
      togglePreventModalClose?.();
    }
  }, [
    currentUser,
    downloadUrlsScope,
    itemClassName,
    refetchItemDownloadUrls,
    removeToastNotification,
    scope,
    showModal,
    showToastNotification,
    togglePreventModalClose,
    withBlob
  ]);

  return (
    <>
      <PureTooltipIconButtonHelper
        className={className}
        dataGa={dataGa}
        disabled={disabled || isLoading}
        i18nText={i18nText}
        i18nTextClassName={i18nTextClassName}
        icon={icon}
        iconClassName={iconClassName}
        onClick={handleClick}
        tooltipI18nText={tooltipI18nText}
        tooltipPlacement={tooltipPlacement}
        tooltipSingleton={tooltipSingleton}
      />

      {isOpen && (
        <SimpleModal
          hideModal={handleHideModal}
          i18nTitle={stringsKeys.filesInDeepArchive}
          id="restore-s3-objects-modal"
          isLoading={isLoading}
          isOpen={isOpen}
          modalSize="3xl"
          withoutButtons
          withoutChildrenWrapper
        >
          <ArchivedFilesList
            archivedFiles={archivedFiles}
            hideModal={handleHideModal}
          />
        </SimpleModal>
      )}
    </>
  );
}

export default DownloadUrlsModalButton;
